import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Highlighter, Baseline } from "lucide-react";
import { useState } from "react";

const SIZES = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48, 72];
const BASE_FONTS = ["Arial", "Calibri", "Cambria", "Times New Roman", "Georgia", "Garamond", "Book Antiqua", "Bookman Old Style",
  "Century Gothic", "Tahoma", "Verdana", "Trebuchet MS", "Comic Sans MS", "Segoe UI", "Arial Black"];
const COLORS = ["000000", "434343", "7F7F7F", "C00000", "FF0000", "FFC000", "00B050", "0070C0", "002060", "7030A0", "E91E63", "FFFFFF"];
const HIGHLIGHTS = ["FFFF00", "00FF00", "00FFFF", "FF00FF", "C0C0C0", "FF0000", "0000FF"];
const ALIGNS = [["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight], ["justify", AlignJustify]];

const keep = (e) => e.preventDefault();

function Btn({ active, onClick, title, testid, children }) {
  return (
    <button type="button" onMouseDown={keep} onClick={onClick} title={title} data-testid={testid}
      className={`h-8 w-8 shrink-0 rounded-lg grid place-items-center transition-colors ${active ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

function Swatches({ icon: Icon, title, testid, colors, current, onPick, allowNone }) {
  const [open, setOpen] = useState(false);
  const pick = (c) => { setOpen(false); onPick(c); };
  return (
    <div className="relative">
      <button type="button" onMouseDown={keep} onClick={() => setOpen((o) => !o)} title={title} data-testid={testid}
        className={`h-8 w-9 shrink-0 rounded-lg flex flex-col items-center justify-center text-slate-600 transition-colors ${open ? "bg-slate-100" : "hover:bg-slate-100"}`}>
        <Icon className="h-4 w-4" />
        <span className="h-1 w-5 rounded-full mt-0.5 border border-slate-200" style={{ background: current ? `#${current}` : "transparent" }} />
      </button>
      {open && (
        <div className="absolute left-0 top-9 z-30 rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid={`${testid}-menu`}>
          <div className="grid grid-cols-6 gap-1.5 w-[11rem]">
            {colors.map((c) => (
              <button key={c} type="button" onClick={() => pick(c)} data-testid={`${testid}-${c}`}
                className="h-6 w-6 rounded-md border border-slate-200 hover:scale-110 transition-transform" style={{ background: `#${c}` }} aria-label={c} />
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            {allowNone ? <button type="button" onClick={() => pick(null)} className="text-[11px] font-semibold text-slate-600 hover:text-rose-600" data-testid={`${testid}-none`}>Tiada sorotan</button> : (
              <label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 cursor-pointer">
                Lain: <input type="color" className="h-6 w-8 cursor-pointer" onChange={(e) => pick(e.target.value.slice(1).toUpperCase())} data-testid={`${testid}-custom`} />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIWordEditorToolbar({ fmt, align, fonts, onFmt, onAlign, onUndo, canUndo }) {
  const fontList = [...new Set([...(fonts || []), ...BASE_FONTS])];
  const sizes = fmt?.sz && !SIZES.includes(fmt.sz) ? [...SIZES, fmt.sz].sort((a, b) => a - b) : SIZES;
  const sel = "h-8 rounded-lg border border-slate-200 bg-white px-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200";
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-2xl border border-slate-200 bg-white px-2 py-1.5 shadow-sm" data-testid="ai-word-editor-toolbar">
      <select className={`${sel} w-[8.5rem]`} value={fmt?.f || ""} onChange={(e) => onFmt("f", e.target.value)} data-testid="ai-word-editor-font" title="Jenis fon">
        {!fmt?.f && <option value="">Fon</option>}
        {fontList.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
      </select>
      <select className={`${sel} w-[4.2rem]`} value={fmt?.sz || ""} onChange={(e) => onFmt("sz", parseFloat(e.target.value))} data-testid="ai-word-editor-size" title="Saiz fon">
        {!fmt?.sz && <option value="">Saiz</option>}
        {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <span className="w-px h-6 bg-slate-200 mx-0.5" />
      <Btn active={fmt?.b} onClick={() => onFmt("b")} title="Tebal (Ctrl+B)" testid="ai-word-editor-bold"><Bold className="h-4 w-4" /></Btn>
      <Btn active={fmt?.i} onClick={() => onFmt("i")} title="Condong (Ctrl+I)" testid="ai-word-editor-italic"><Italic className="h-4 w-4" /></Btn>
      <Btn active={fmt?.u} onClick={() => onFmt("u")} title="Garis bawah (Ctrl+U)" testid="ai-word-editor-underline"><Underline className="h-4 w-4" /></Btn>
      <Swatches icon={Baseline} title="Warna fon" testid="ai-word-editor-color" colors={COLORS} current={fmt?.c} onPick={(c) => onFmt("c", c)} />
      <Swatches icon={Highlighter} title="Sorotan" testid="ai-word-editor-highlight" colors={HIGHLIGHTS} current={fmt?.h} onPick={(c) => onFmt("h", c)} allowNone />
      <span className="w-px h-6 bg-slate-200 mx-0.5" />
      {ALIGNS.map(([a, Icon]) => (
        <Btn key={a} active={align === a} onClick={() => onAlign(a)} title={`Jajar ${a}`} testid={`ai-word-editor-align-${a}`}><Icon className="h-4 w-4" /></Btn>
      ))}
      <span className="w-px h-6 bg-slate-200 mx-0.5" />
      <button type="button" onMouseDown={keep} onClick={onUndo} disabled={!canUndo} title="Buat asal format" data-testid="ai-word-editor-undo"
        className="h-8 w-8 rounded-lg grid place-items-center text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors"><Undo2 className="h-4 w-4" /></button>
    </div>
  );
}
